import { Component, OnInit } from '@angular/core';
import * as moment from 'moment';
import * as FileSaver from 'file-saver';
import { FiltroDealers } from './filtro-dealer.one-page';
import { OnePageFiltroService } from './one-page-filtro.service';
import { OnePageService } from './one-page.service';
import { ExportExcelService } from '../../../shared/excel-service/export-excel.service';

interface OnePageLinha {
  descricao: string;
  tipo: 'valor' | 'quantidade' | 'percentual' | 'texto';
  valores: { [coluna: string]: any };
  destaque?: boolean;
  /** Códigos de Tipo de O.S. da ET (coluna B) — usado como referência/tooltip no Pós-Vendas */
  codigos?: string;
}

interface OnePageSecao {
  titulo: string;
  subtitulo?: string;
  colunas: string[];
  linhas: OnePageLinha[];
  classe: string;
}

interface OnePageBloco {
  faturamento?: OnePageSecao;
  margem?: OnePageSecao;
}

@Component({
  selector: 'app-one-page',
  templateUrl: './one-page.component.html',
  styleUrls: ['./one-page.component.scss']
})
export class OnePageComponent implements OnInit {

  loading = false;

  dataInicial: Date;
  dataFinal: Date;

  displayFiltrosDealers = false;
  filtrosDealers: FiltroDealers;

  /**
   * Colunas de motorização — ÚNICAS para todas as seções conforme a ET (Sugestão NBS).
   * Derivadas do chassi (posições 6 e 7):
   *   I -> ICE | M -> MHEV | P -> PHEV | B -> BEV | senão -> N.INFORM.
   */
  colunasMotorizacao: string[] = ['BEV', 'PHEV', 'MHEV', 'ICE', 'N.INFORM.'];

  blocos: OnePageBloco[] = [];
  ordensServico: OnePageSecao[] = [];

  taxaAbsorcao = '';
  nomeModulo = 'one-page-volvo';

  constructor(
    private service: OnePageService,
    private filtroService: OnePageFiltroService,
    private exportExcelService: ExportExcelService
  ) {
    this.filtrosDealers = new FiltroDealers(this.filtroService);
    this.tratarData();
  }

  ngOnInit() {
    this.filtrosDealers.carregar();
    this.montarLayout();
  }

  tratarData() {
    const hoje = new Date();
    this.dataInicial = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    this.dataFinal = hoje;
  }

  selecionarDealer() {
    this.displayFiltrosDealers = true;
  }

  onAdicionarDealerFiltro(dealer: any) {
    dealer.selecionado = true;
  }

  removerDealer(dealer: any) {
    dealer.selecionado = false;
  }

  limparFiltros() {
    this.tratarData();
    this.filtrosDealers.dealers.forEach(dealer => (dealer.selecionado = false));
  }

  limpaDataInicial() {
    const hoje = new Date();
    this.dataInicial = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  }

  limpaDataFinal() {
    this.dataFinal = new Date();
  }

  filtroAPI() {
    const filtros = {
      dataInicial: this.dataInicial ? moment(this.dataInicial).format('YYYY-MM-DD') : '',
      dataFinal: this.dataFinal ? moment(this.dataFinal).format('YYYY-MM-DD') : '',
      dealers: this.filtrosDealers.selecionados().map(dealer => dealer.id).join(',')
    };

    this.loading = true;

    this.service.buscarDados(filtros)
      .then(resposta => {
        this.preencherValores(resposta);
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
      });
  }

  // ---------------------------------------------------------------------------
  // Export Excel — gera HTML com estilos (bordas, negrito, destaque) e salva
  // como .xls. O Excel abre respeitando a formatação, sem dependência nova.
  // ---------------------------------------------------------------------------

  exportExcel() {
    const bordas = 'border:1px solid #202020;';
    const estiloTitulo = `${bordas}background:#263238;color:#ffffff;font-weight:bold;`;
    const estiloSecao = `${bordas}background:#d9d9d9;font-weight:bold;`;
    const estiloCabecalho = `${bordas}background:#f2f2f2;font-weight:bold;text-align:center;`;
    const estiloDescricao = `${bordas}text-align:left;`;
    const estiloValor = `${bordas}text-align:right;`;
    const estiloTotal = `${bordas}background:#263238;color:#ffffff;font-weight:bold;text-align:right;`;
    const estiloTotalDesc = `${bordas}background:#263238;color:#ffffff;font-weight:bold;text-align:left;`;

    let html = '<table style="border-collapse:collapse;font-family:Arial;font-size:12px;">';

    const secaoHtml = (secao: OnePageSecao): string => {
      const linhas = this.linhasPreenchidas(secao);
      if (!linhas.length) {
        return '';
      }

      const titulo = secao.subtitulo
        ? `${secao.titulo} - ${secao.subtitulo}`
        : secao.titulo;

      let bloco = `<tr><td style="${estiloSecao}" colspan="${secao.colunas.length + 1}">${titulo}</td></tr>`;

      bloco += `<tr><td style="${estiloCabecalho}"></td>`;
      secao.colunas.forEach(coluna => {
        bloco += `<td style="${estiloCabecalho}">${coluna}</td>`;
      });
      bloco += '</tr>';

      linhas.forEach(linha => {
        const desc = linha.destaque ? estiloTotalDesc : estiloDescricao;
        const val = linha.destaque ? estiloTotal : estiloValor;

        bloco += `<tr><td style="${desc}">${linha.descricao}</td>`;
        secao.colunas.forEach(coluna => {
          bloco += `<td style="${val}">${this.getValor(linha, coluna)}</td>`;
        });
        bloco += '</tr>';
      });

      bloco += `<tr><td colspan="${secao.colunas.length + 1}"></td></tr>`;
      return bloco;
    };

    // FATURAMENTO
    html += `<tr><td style="${estiloTitulo}" colspan="6">FATURAMENTO</td></tr>`;
    this.blocos.forEach(bloco => {
      if (bloco.faturamento) {
        html += secaoHtml(bloco.faturamento);
      }
    });

    // MARGEM BRUTA
    html += `<tr><td style="${estiloTitulo}" colspan="6">MARGEM BRUTA</td></tr>`;
    this.blocos.forEach(bloco => {
      if (bloco.margem) {
        html += secaoHtml(bloco.margem);
      }
    });

    // ORDENS DE SERVIÇO
    this.ordensServico.forEach(secao => {
      html += secaoHtml(secao);
    });

    // Taxa de Absorção
    html += `<tr><td style="${estiloSecao}">Taxa Absorção</td>`
          + `<td style="${estiloValor}">${this.taxaAbsorcao || ''}</td></tr>`;

    html += '</table>';

    const conteudo = '<html xmlns:x="urn:schemas-microsoft-com:office:excel"><head>'
      + '<meta charset="UTF-8"></head><body>' + html + '</body></html>';

    const blob = new Blob([conteudo], { type: 'application/vnd.ms-excel;charset=utf-8' });
    FileSaver.saveAs(blob, 'modulo one-page-volvo.xls');
  }

  /** Linhas que a API preencheu. */
  linhasPreenchidas(secao: OnePageSecao): OnePageLinha[] {
    if (!secao) {
      return [];
    }
    return secao.linhas.filter(linha =>
      secao.colunas.some(coluna => {
        const valor = linha.valores[coluna];
        return valor !== null && valor !== undefined && valor !== '';
      })
    );
  }

  // ---------------------------------------------------------------------------
  // Montagem do layout (estrutura fixa vinda da ET)
  // ---------------------------------------------------------------------------

  private linha(
    descricao: string,
    tipo: 'valor' | 'quantidade' | 'percentual' | 'texto' = 'valor',
    destaque: boolean = false,
    codigos?: string
  ): OnePageLinha {
    const valores: { [coluna: string]: any } = {};
    this.colunasMotorizacao.forEach(coluna => (valores[coluna] = ''));
    return { descricao, tipo, valores, destaque, codigos };
  }

  private montarLayout() {
    const col = this.colunasMotorizacao;

    // ---- DEPTO VENDAS NOVOS (só faturamento) ----
    const novosFaturamento: OnePageSecao = {
      titulo: 'DEPTO VENDAS NOVOS',
      subtitulo: 'Faturamento',
      colunas: col,
      classe: 'secao-vendas-novos',
      linhas: [
        this.linha('Quantidade Vendas - VN', 'quantidade'),
        this.linha('Faturamento Vendas - VN', 'valor')
      ]
    };

    // ---- DEPTO VENDAS SEMINOVOS ----
    const seminovosFaturamento: OnePageSecao = {
      titulo: 'DEPTO VENDAS SEMINOVOS',
      subtitulo: 'Faturamento',
      colunas: col,
      classe: 'secao-seminovos',
      linhas: [
        this.linha('Quantidade Vendas - VSN Volvo', 'quantidade'),
        this.linha('Quantidade Vendas - VSN Outras Marcas', 'quantidade'),
        this.linha('Faturamento VSN Volvo', 'valor'),
        this.linha('Faturamento VSN Outras Marcas', 'valor')
      ]
    };

    const seminovosMargem: OnePageSecao = {
      titulo: 'DEPTO VENDAS SEMINOVOS',
      subtitulo: 'Margem Bruta',
      colunas: col,
      classe: 'secao-seminovos',
      linhas: [
        this.linha('Margem Bruta VSN Volvo', 'valor'),
        this.linha('Margem Bruta VSN Outras Marcas', 'valor')
      ]
    };

    // ---- DEPTO PÓS VENDAS (linhas com códigos de Tipo O.S. da ET) ----
    const posVendasLinhas = (): OnePageLinha[] => [
      this.linha('Clientes', 'valor', false, 'VC'),
      this.linha('Garantia', 'valor', false, 'VG, VFG, VGW'),
      this.linha('Funilaria', 'valor', false, 'VFF, VFP, VFS, VF4'),
      this.linha('Seguradora', 'valor', false, 'VS'),
      this.linha('Acessorio', 'valor', false, 'VW, VO, VA, VV'),
      this.linha('Retorno', 'valor', false, 'VSK'),
      this.linha('Interna + Cortesia', 'valor', false,
        'VC1, VC2, VC4, VC5, VC6, VC8, VF1, VF2, VF3, VF5, VF6, VF7'),
      this.linha('Revisao', 'valor', false, 'VM, VY'),
      this.linha('TOTAL - Serviços', 'valor', true)
    ];

    const posVendasFaturamento: OnePageSecao = {
      titulo: 'DEPTO PÓS VENDAS',
      subtitulo: 'Faturamento',
      colunas: col,
      classe: 'secao-pos-vendas',
      linhas: posVendasLinhas()
    };

    const posVendasMargem: OnePageSecao = {
      titulo: 'DEPTO PÓS VENDAS',
      subtitulo: 'Margem Bruta',
      colunas: col,
      classe: 'secao-pos-vendas',
      linhas: posVendasLinhas()
    };

    // ---- PEÇAS ----
    const pecasLinhas = (): OnePageLinha[] => [
      this.linha('Peças Balcao', 'valor'),
      this.linha('Peças Oficina', 'valor'),
      this.linha('Acessórios Balcao', 'valor'),
      this.linha('Acessórios Oficina', 'valor'),
      this.linha('Lubrificantes', 'valor'),
      this.linha('Pneus', 'valor'),
      this.linha('Wall Box - WEG', 'valor'),
      this.linha('Wall Box - ATRIA', 'valor'),
      this.linha('Life Style', 'valor'),
      this.linha('Insulfim', 'valor'),
      this.linha('Outros', 'valor'),
      this.linha('TOTAL - Peças', 'valor', true)
    ];

    const pecasFaturamento: OnePageSecao = {
      titulo: 'PEÇAS',
      subtitulo: 'Faturamento',
      colunas: col,
      classe: 'secao-pecas',
      linhas: pecasLinhas()
    };

    const pecasMargem: OnePageSecao = {
      titulo: 'PEÇAS',
      subtitulo: 'Margem Bruta',
      colunas: col,
      classe: 'secao-pecas',
      linhas: pecasLinhas()
    };

    this.blocos = [
      { faturamento: novosFaturamento },
      { faturamento: seminovosFaturamento, margem: seminovosMargem },
      { faturamento: posVendasFaturamento, margem: posVendasMargem },
      { faturamento: pecasFaturamento, margem: pecasMargem }
    ];

    // ---- ORDENS DE SERVIÇO (largura cheia) ----
    const osLinhas = (): OnePageLinha[] => [
      this.linha('OS Internas', 'quantidade'),
      this.linha('OS Clientes - Mecânica', 'quantidade'),
      this.linha('Os Clientes - Funilaria', 'quantidade'),
      this.linha('OS Clientes - Seguradora', 'quantidade')
    ];

    this.ordensServico = [
      {
        titulo: 'Quantidade de Ordem de Serviços - Encerradas',
        colunas: col,
        classe: 'secao-os',
        linhas: osLinhas()
      },
      {
        titulo: 'Quantidade de Ordem de Serviços - Em aberto',
        colunas: col,
        classe: 'secao-os',
        linhas: osLinhas()
      }
    ];
  }

  // ---------------------------------------------------------------------------
  // Integração com a API (preenche as linhas já montadas)
  // ---------------------------------------------------------------------------

  /**
   * Mapeia a resposta do back nas linhas da tela.
   * Por enquanto apenas a seção Depto Vendas Novos (bloco 0 / faturamento).
   */
  private preencherValores(resposta: any) {
    if (!resposta) {
      return;
    }

    if (resposta.vendasNovos) {
      this.aplicarSecao(this.blocos[0].faturamento, resposta.vendasNovos.linhas);
    }

    if (resposta.seminovosFaturamento) {
      this.aplicarSecao(this.blocos[1].faturamento, resposta.seminovosFaturamento.linhas);
    }

    if (resposta.seminovosMargem) {
      this.aplicarSecao(this.blocos[1].margem, resposta.seminovosMargem.linhas);
    }

    if (resposta.posVendasFaturamento) {
      this.aplicarSecao(this.blocos[2].faturamento, resposta.posVendasFaturamento.linhas);
    }

    if (resposta.posVendasMargem) {
      this.aplicarSecao(this.blocos[2].margem, resposta.posVendasMargem.linhas);
    }

    if (resposta.pecasFaturamento) {
      this.aplicarSecao(this.blocos[3].faturamento, resposta.pecasFaturamento.linhas);
    }

    if (resposta.pecasMargem) {
      this.aplicarSecao(this.blocos[3].margem, resposta.pecasMargem.linhas);
    }

    if (resposta.osEncerradas) {
      this.aplicarSecao(this.ordensServico[0], resposta.osEncerradas.linhas);
    }

    if (resposta.osEmAberto) {
      this.aplicarSecao(this.ordensServico[1], resposta.osEmAberto.linhas);
    }
  }

  /**
   * Casa cada linha da API (por 'descricao') com a linha da tela e joga
   * cada motorização na coluna correspondente.
   */
  private aplicarSecao(secao: OnePageSecao, linhas: any[]) {
    if (!secao || !linhas) {
      return;
    }

    const mapa: { [coluna: string]: string } = {
      'BEV': 'bev',
      'PHEV': 'phev',
      'MHEV': 'mhev',
      'ICE': 'ice',
      'N.INFORM.': 'naoInformado'
    };

    linhas.forEach(linhaApi => {
      const alvo = secao.linhas.find(l => l.descricao === linhaApi.descricao);
      if (alvo) {
        secao.colunas.forEach(coluna => {
          alvo.valores[coluna] = linhaApi[mapa[coluna]];
        });
      }
    });

    this.calcularTotal(secao);
  }

  /**
   * Soma vertical: preenche a linha de destaque (TOTAL) com a soma das demais
   * linhas em cada coluna de motorização. Serve para TOTAL - Serviços, TOTAL - Peças, etc.
   */
  private calcularTotal(secao: OnePageSecao) {
    const totalLinha = secao.linhas.find(l => l.destaque);
    if (!totalLinha) {
      return;
    }

    secao.colunas.forEach(coluna => {
      let soma = 0;
      secao.linhas.forEach(linha => {
        if (!linha.destaque) {
          const valor = Number(linha.valores[coluna]);
          if (!isNaN(valor)) {
            soma += valor;
          }
        }
      });
      totalLinha.valores[coluna] = soma;
    });
  }

  getValor(linha: OnePageLinha, coluna: string): string {
    if (!linha || !linha.valores) {
      return '';
    }

    const valor = linha.valores[coluna];

    if (valor === null || valor === undefined || valor === '') {
      return '';
    }

    if (linha.tipo === 'quantidade' || linha.tipo === 'texto') {
      return valor.toString();
    }

    if (linha.tipo === 'percentual') {
      return valor + '%';
    }

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 0
    }).format(valor);
  }
}